/* eslint-disable max-len */
import { CloseOutlined, SyncOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form, Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import {
  patchGenerarDeclaracion,
  postDeclaracionAlcoholes,
  postRechazarDeclaracion,
} from 'api/recaudacion';
import { Button, Text } from 'components';
import Select from 'components/PaginatedSelect';
import propTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { actualizacionTotal, getINPC, recargosTotal } from 'utils/autocalculos';
import { DATE_FORMAT } from 'utils/formatters';
import _logger from 'utils/logger';
import { toMoment } from 'utils/normalizers';
import { CURP_PATTERN, RFC_PATTERN } from 'utils/patterns';
import round from 'utils/round';
import ModalConfirm from './ModalConfirm';

const _rules = {
  required: (message) => ({
    required: true,
    message,
  }),
  numeric: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+([.][0-9]{1,2})?$/;
        if (!value) return Promise.resolve();
        if (!Number.isInteger(parseFloat(value))) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  mayorCero: [
    {
      validator: async (_, value) => {
        if (value <= 0) {
          throw new Error('El valor debe ser mayor a 0.');
        }
      },
    },
  ],
  entero: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+$/;
        if (!value) return Promise.resolve();
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  rfc: [{ pattern: RFC_PATTERN, message: ' Ingrese un RFC valido.' }],
  curp: [{ pattern: CURP_PATTERN, message: ' Ingrese una CURP valida.' }],
};

const initialValues = {
  es_declaracion_en_ceros: false,
  retenciones: 0,
  actualizacion: 0,
  recargos: 0,
  multas: 0,
  cantidad_a_pagar: 0,
  cantidad_a_favor: 0,
  total_de_contribuciones: 0,
  impuesto: 0,
  impuesto_a_cargo: 0,
  monto_pagado_con_anterioridad: 0,
};

const calcularImpuesto = (montoMensual, porcentajePeriodo) => {
  if (!montoMensual) return 1 * porcentajePeriodo;
  return round((parseFloat(montoMensual) * porcentajePeriodo) || 0)[0];
};

const calcularImpuestoACargo = (impuesto, retenciones) => {
  const _impuesto = +impuesto || 0;
  const _retenciones = +retenciones || 0;
  return round(_impuesto - _retenciones)[0];
};

const calcularTotalContribuciones = (impuestoACargo, actualizacion, recargos, multas) => {
  const total = [
    impuestoACargo,
    actualizacion,
    recargos,
    multas,
  ].map(Number).reduce((a, b) => a + b, 0);
  return round(total || 0)[0];
};

const calcularCantidadACargo = (totalContribuciones, montoPagadoAnterioridad) => {
  const _montoPagado = +montoPagadoAnterioridad || 0;
  const _totalContribuciones = +totalContribuciones || 0;
  const _cantidadACargo = _totalContribuciones - _montoPagado;

  if (_cantidadACargo > 0) {
    return {
      cantidadACargo: round(_cantidadACargo)[0],
      cantidadAFavor: 0,
    };
  }

  return {
    cantidadACargo: 0,
    cantidadAFavor: round((_montoPagado * 1) - _totalContribuciones)[0],
  };
};

const FormVentaBebidasAlcoholicas = ({
  padron,
  statusDeclaracion,
  visible,
  setVisibleForm,
  selectedCargos,
  isComplementaria,
  callback,
  callbackCancel,
  declaracionId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isZeroState, setIsZeroState] = useState(false);
  const [disableInput, setDisableInput] = useState(false);
  const [porcentajePeriodo, setPorcentajePeriodo] = useState(0);
  const [INPC, setINPC] = useState(0);

  // watch values
  const _impuesto = Form.useWatch('impuesto_a_cargo', form);
  const actualizacion = Form.useWatch('actualizacion', form);
  const recargos = Form.useWatch('recargos', form);
  const multas = Form.useWatch('multas', form);
  const monto_pagado_con_anterioridad = Form.useWatch('monto_pagado_con_anterioridad', form);
  const total_de_contribuciones = Form.useWatch('total_de_contribuciones', form);

  const calcularActualizacion = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ actualizacion: 0 });
        return;
      }

      const _actualizacion = await actualizacionTotal({
        importe_cargo: importeNumerico,
        tipo_de_cargo: selectedCargos[0],
        INPC,
      });

      form.setFieldsValue({ actualizacion: round(_actualizacion || 0)[0] });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ actualizacion: 0 });
    }
  };

  const calcularRecargos = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ recargos: 0 });
        return;
      }

      const _impuestoDeterminado = typeof _impuesto === 'number'
        ? _impuesto
        : parseFloat(_impuesto);

      const _recargos = await recargosTotal({
        tipo_de_cargo: selectedCargos[0],
        importe_cargo: importeNumerico,
        base_gravada: _impuestoDeterminado,
        fecha_de_vencimiento: selectedCargos[0]?.fecha_de_vencimiento,
      });

      form.setFieldsValue({ recargos: round(_recargos || 0)[0] });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ recargos: 0 });
    }
  };

  const calculateINPC = async () => {
    if (selectedCargos.length > 0) {
      const periodo = selectedCargos[0]?.periodo_fiscal?.periodo || '2024';
      const mes = selectedCargos[0]?.identificador_periodo;
      const _inpc = await getINPC(periodo, mes);
      setINPC(_inpc?.[0] ?? {});
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    await postRechazarDeclaracion({
      declaracion: declaracionId,
      canal_de_pago: 5,
    });
    setLoading(false);
  };

  const handleUnload = (event) => {
    const message = 'Se perderán los datos capturados, ¿Estás seguro de salir?';
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.returnValue = message; // Esto mostrará el cuadro de confirmación
    return message;
  };

  // calculamos la actualizacion
  useEffect(() => {
    const importe = typeof _impuesto === 'number'
      ? _impuesto
      : parseFloat(_impuesto);

    if (!Number.isNaN(importe)) {
      calcularActualizacion({ importe });
    }
  }, [_impuesto]);

  // calculamos los recargos
  useEffect(() => {
    const importe = typeof actualizacion === 'number'
      ? actualizacion
      : parseFloat(actualizacion);

    if (!Number.isNaN(importe)) {
      calcularRecargos({ importe });
    }
  }, [actualizacion, _impuesto]);

  // calculamos el total de contribuciones
  useEffect(() => {
    try {
      const impuestoACargo = typeof _impuesto === 'number'
        ? _impuesto
        : parseFloat(_impuesto);

      if (Number.isNaN(impuestoACargo)) {
        form.setFieldsValue({ total_de_contribuciones: 0 });
        return;
      }

      const total = calcularTotalContribuciones(
        impuestoACargo,
        actualizacion,
        recargos,
        multas,
      );

      form.setFieldsValue({ total_de_contribuciones: total });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ total_de_contribuciones: 0 });
    }
  }, [_impuesto, actualizacion, recargos, multas]);

  // calculamos la cantidad a cargo
  useEffect(() => {
    const montoPagadoConAnterioridad = parseFloat(monto_pagado_con_anterioridad) || 0;
    const totalDeContribuciones = parseFloat(total_de_contribuciones) || 0;
    const cantidadACargo = totalDeContribuciones - montoPagadoConAnterioridad;
    if (cantidadACargo > 0) {
      form.setFieldsValue({
        cantidad_a_pagar: round(cantidadACargo)[0],
        cantidad_a_favor: 0,
      });
    } else {
      form.setFieldsValue({
        cantidad_a_favor: round(montoPagadoConAnterioridad * 1 - totalDeContribuciones)[0],
        cantidad_a_pagar: 0,
      });
    }
  }, [monto_pagado_con_anterioridad, total_de_contribuciones]);

  useEffect(() => {
    if (visible) {
      calculateINPC();
    }
    window.addEventListener('unload', handleUnload);
    window.addEventListener('beforeunload', handleCancel);

    return () => {
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('beforeunload', handleCancel);
    };
  }, [visible]);

  // Resetea el formulario cuando se cierra.
  useEffect(() => {
    if (visible) {
      const _porcentaje = ((selectedCargos[0]?.importe || 5) / 100);
      setPorcentajePeriodo(_porcentaje);
      form.resetFields();
    }
  }, [visible]);

  const patchDeclaracionStatus = async (values) => {
    try {
      let statusId = null;

      const statusObject = {
        generada: statusDeclaracion.find((status) => !status.declaracion_presentada && !status.declaracion_en_ceros)?.id,
        en_ceros: statusDeclaracion.find((status) => status.declaracion_en_ceros)?.id,
        sin_importe: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidades a Pagar y a Favor en Cero')?.id || 0,
        saldo_favor: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidad a Favor')?.id || 0,
      };

      if (isZeroState) {
        statusId = statusObject.en_ceros;
      } else if (parseFloat(values.cantidad_a_favor) > 0) {
        statusId = statusObject.saldo_favor;
      } else if (parseFloat(values.cantidad_a_pagar) <= 0) {
        statusId = statusObject.sin_importe;
      } else {
        statusId = statusObject.generada;
      }
      const request = await patchGenerarDeclaracion({
        id: values.declaracion_empresarial,
        estatus_de_declaracion_empresarial: statusId,
        razones_para_presentar_en_ceros: values.razones_para_presentar_en_ceros,
      });

      if (!request) throw new Error('Error al actualizar la declaración.');
    } catch (error) {
      _logger(error);
    }
  };

  // Envia los datos del formulario.
  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const _padronValues = {
        razon_social_nombre_completo:
        padron?.empresa?.contribuyente?.razon_social
        || padron?.empresa?.contribuyente?.nombre_completo
        || '',
        rfc: padron?.empresa?.contribuyente?.rfc || '',
        curp: padron?.empresa?.contribuyente?.curp || '',
        nombre: padron?.empresa?.contribuyente?.nombre_completo || '',
        nombre_completo: padron?.empresa?.contribuyente?.nombre_completo || '',
        apellido_paterno: padron?.empresa?.contribuyente?.apellido_paterno || '',
        apellido_materno: padron?.empresa?.contribuyente?.apellido_materno || '',
      };

      let _values = {
        ...values,
        ..._padronValues,
        declaracion_empresarial: declaracionId,
        numero_de_sucursales_declaradas: values.numero_de_sucursales_declaradas === '' ? 0 : values.numero_de_sucursales_declaradas,
        entidad: 1,
        fecha_del_pago_anterior: isComplementaria && !isZeroState ? values.fecha_del_pago_anterior.format(DATE_FORMAT) : null,
      };

      if (isZeroState) {
        _values = {
          ..._values,
          actualizacion: 0,
          cantidad_a_favor: 0,
          cantidad_a_pagar: 0,
          impuesto: 0,
          impuesto_a_cargo: 0,
          monto_mensual: 0,
          monto_pagado_con_anterioridad: 0,
          multas: 0,
          recargos: 0,
          retenciones: 0,
          total_de_contribuciones: 0,
        };
      }

      if (!isComplementaria) {
        _values = {
          ..._values,
          fecha_del_pago_anterior: null,
          monto_pagado_con_anterioridad: 0,
        };
      }
      if (isZeroState && typeof _values.monto_mensual === 'undefined') {
        _values.monto_mensual = 0;
      }

      await patchDeclaracionStatus(_values);
      await postDeclaracionAlcoholes(_values);

      callback(_values.cantidad_a_pagar <= 0);
      setIsZeroState(false);
      setIsVisibleModal(false);
      setVisibleForm(false);
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  const validacionesDeOperaciones = async (values) => {
    const retencion_rounded = round(+values.retenciones)[0];
    const impuesto_rounded = round(+values.impuesto)[0];
    const diferencia = impuesto_rounded - retencion_rounded;

    try {
      if (diferencia < 0 && !isZeroState) {
        await form.validateFields(['impuesto_a_cargo']);
        await form.validateFields(['retenciones']);

        form.setFields([
          {
            name: 'impuesto_a_cargo',
            errors: ['El valor no puede ser mayor al impuesto correspondiente.'],
          },
          {
            name: 'retenciones',
            errors: ['El valor no puede ser mayor al impuesto correspondiente.'],
          },
        ]);
      } else if (diferencia >= 0 && !isZeroState) {
        await form.validateFields(['impuesto_a_cargo']);
        await form.validateFields(['retenciones']);

        form.setFields([
          {
            name: 'impuesto_a_cargo',
            errors: [],
          },
          {
            name: 'retenciones',
            errors: [],
          },
        ]);
      }

      if (diferencia <= 0 && !isZeroState) {
        form.setFieldsValue({ actualizacion: 0, recargos: 0, multas: 0 });
      }

      const resultado = parseFloat(impuesto_rounded - retencion_rounded);
      const shouldEnableInput = resultado > 0 && !isZeroState;
      setDisableInput(!shouldEnableInput);
    } catch (error) {
      _logger(error);
    }
  };

  // Calcula el total de la cantidad a pagar.
  const total = () => {
    const values = form.getFieldsValue();
    const impuesto = calcularImpuesto(values.monto_mensual, porcentajePeriodo);
    const impuestoACargo = calcularImpuestoACargo(impuesto, values.retenciones);
    const totalContribuciones = calcularTotalContribuciones(
      impuestoACargo,
      values.actualizacion,
      values.recargos,
      values.multas,
    );

    const { cantidadACargo, cantidadAFavor } = calcularCantidadACargo(
      totalContribuciones,
      values.monto_pagado_con_anterioridad,
    );

    form.setFieldsValue({
      impuesto,
      impuesto_a_cargo: impuestoACargo,
      total_de_contribuciones: totalContribuciones,
      cantidad_a_pagar: cantidadACargo,
      cantidad_a_favor: cantidadAFavor,
    });

    validacionesDeOperaciones({
      impuesto,
      retenciones: values.retenciones,
      impuesto_a_cargo: impuestoACargo,
    });
  };

  return (
    <>
      <Modal
        centered
        title={(
          <Row justify="space-between">
            <Text $accent>
              IMPUESTO A LA VENTA DE BEBIDAS CON CONTENIDO ALCOHÓLICO
            </Text>
          </Row>
        )}
        visible={visible}
        closable={false}
        footer={(
          <Row justify="space-between" gutter={[10, 15]}>
            <Col xs={24} sm={24} md={12} style={{ textAlign: 'left' }}>
              <Text $accent>* Este campo es obligatorio</Text>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => callbackCancel()}
                loading={loading}
                $accent
                icon={<CloseOutlined />}
              >
                <Text>
                  Cancelar
                </Text>
              </Button>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                type="primary"
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => {
                  form.validateFields().then(() => {
                    setIsVisibleModal(true);
                  }).catch(() => {
                    setIsVisibleModal(false);
                  });
                }}
                loading={loading}
                icon={<SyncOutlined />}
                $accent
              >
                <Text>
                  Generar
                </Text>
              </Button>
            </Col>
          </Row>
        )}
        width="90vw"
        style={{ maxWidth: '1200px' }}
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={initialValues}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item
                  name="es_declaracion_en_ceros"
                  label="¿Es Declaración en ceros?"
                  required
                >
                  <Select
                    placeholder="Selecciona una opción"
                    trueFalse
                    allowClear={false}
                    onChange={(value) => setIsZeroState(value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item
                  hasFeedback
                  name="numero_de_sucursales_declaradas"
                  tooltip="En caso de tener sucursales anotar el número de las que declara"
                  rules={[..._rules.numeric]}
                  label="Número de sucursales"
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {isZeroState ? (
                <Col xs={24}>
                  <Form.Item
                    name="razones_para_presentar_en_ceros"
                    label="Razones por las que presenta en ceros"
                    rules={[_rules.required('El campo es requerido')]}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                </Col>
              ) : (
                <Col xs={24}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Concepto</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Ingreso mensual</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="monto_mensual"
                              tooltip="Monto mensual por ventas de bebidas con contenido alcohólico"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.numeric,
                                ..._rules.mayorCero,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                                onBlur={(val) => {
                                  if (!Number.isInteger(parseFloat(val.target.value))) {
                                    const redondeado = val.target.value - Math.floor(parseFloat(val.target.value));
                                    if (parseFloat(redondeado).toFixed(2) >= 0.51) {
                                      form.setFieldsValue({
                                        monto_mensual: parseFloat(Math.ceil(val.target.value)).toFixed(2),
                                        impuesto: parseFloat(Math.ceil(val.target.value) * porcentajePeriodo).toFixed(2),
                                      });
                                    } else {
                                      form.setFieldsValue({
                                        monto_mensual: parseFloat(Math.floor(val.target.value)).toFixed(2),
                                        impuesto: parseFloat(Math.floor(val.target.value) * porcentajePeriodo).toFixed(2),
                                      });
                                    }
                                  }
                                  total();
                                }}
                                onChange={(value) => {
                                  if (value.target.value === '') {
                                    form.setFieldsValue({ impuesto: 1 * porcentajePeriodo });
                                  } else {
                                    const val_monto = parseFloat(value.target.value) * porcentajePeriodo;
                                    form.setFieldsValue({ impuesto: val_monto.toFixed(2) });
                                  }
                                  total();
                                }}
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto correspondiente</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Retenciones</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="retenciones"
                              tooltip="Retenciones realizadas por el comerciante, almacenista o distribuidor"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto a cargo</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto_a_cargo"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Actualización</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="actualizacion"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled={disableInput} onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Recargos</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="recargos"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled={disableInput} onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Multas</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="multas"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled={disableInput} onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Total de contribuciones</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="total_de_contribuciones"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        {isComplementaria && (
                          <>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Fecha del pago anterior</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  name="fecha_del_pago_anterior"
                                  rules={[_rules.required('El campo es requerido')]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <DatePicker
                                    format={DATE_FORMAT}
                                    disabledDate={(d) => d.isAfter(toMoment(new Date()))}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Monto pagado con anterioridad</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  hasFeedback
                                  name="monto_pagado_con_anterioridad"
                                  rules={[
                                    _rules.required('El valor tiene que ser 0 o mayor que 0'),
                                    ..._rules.numeric,
                                  ]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input prefix="$" onChange={() => total()} />
                                </Form.Item>
                              </td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Cantidad a pagar</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_pagar"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Cantidad a favor</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_favor"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              )}
            </Row>
          </Form>
        </Spin>
      </Modal>
      <ModalConfirm
        visible={isVisibleModal}
        setVisible={setIsVisibleModal}
        onFinish={() => onFinish()}
        body="Al generar la declaración no podrá ser modificada. ¿Desea continuar?"
        btnText="Si, generar declaración"
        loading={loading}
      />
    </>
  );
};

FormVentaBebidasAlcoholicas.propTypes = {
  padron: propTypes.shape({
    empresa: propTypes.shape({
      contribuyente: propTypes.shape({
        razon_social: propTypes.string,
        rfc: propTypes.string,
        nombre_completo: propTypes.string,
        curp: propTypes.string,
        nombre: propTypes.string,
        apellido_paterno: propTypes.string,
        apellido_materno: propTypes.string,
        ciudadano: propTypes.shape({
          curp: propTypes.string,
          nombre: propTypes.string,
          apellido_paterno: propTypes.string,
          apellido_materno: propTypes.string,
        }),
      }),
    }),
  }).isRequired,
  selectedCargos: propTypes.arrayOf({
    fecha_de_vencimiento: propTypes.string,
    importe: propTypes.number,
    identificador_periodo: propTypes.string,
    periodo_fiscal: propTypes.shape({
      periodo: propTypes.oneOfType([propTypes.string, propTypes.number]),
    }),
  }),
  statusDeclaracion: propTypes.number.isRequired,
  visible: propTypes.bool,
  isComplementaria: propTypes.bool,
  setVisibleForm: propTypes.func.isRequired,
  callback: propTypes.func.isRequired,
  callbackCancel: propTypes.func,
  declaracionId: propTypes.func.isRequired,
};

FormVentaBebidasAlcoholicas.defaultProps = {
  isComplementaria: false,
  visible: false,
  selectedCargos: [],
  callbackCancel: () => {},
};

export default FormVentaBebidasAlcoholicas;
